import { read, TABLE } from '../data/db';
import { hypotheses, matches, observations, profile, teams } from '../data/repos';
import { deriveMatchMinute } from '../domain/clock';
import type { DiscoveryPageRow } from '../domain/discovery';
import { isInMatch } from '../domain/machine';
import { toHypothesisRow } from '../domain/setup';
import type { PreMatchCandidate } from '../domain/setup';
import type { AddDiscoveryHypothesisCommand, IncrementCommand } from '../domain/commands';
import type {
  HypothesisPhase,
  MatchRow,
  ObservationRow,
  Status,
  StatusOverride,
  TemplateRow,
} from '../types';
import { dispatch } from './bus';
import type { DispatchResult } from './bus';
import { newId } from './ids';

/**
 * DISC-1 … DISC-7 — the **I/O half** of Discovery: reads for the surface, dispatches for its rows.
 *
 * §1.2 forbids `ui/` from importing `data/`, so every row the pager renders arrives through here and
 * every write leaves through `dispatch`. The *decisions* are not here — `domain/discovery.ts` owns
 * DISC-2's page set and DISC-7's gate, `domain/order.ts` owns DISC-4's comparator,
 * `domain/gesture.ts` owns DD-1's classification, and `domain/commands/discovery.ts` owns DISC-5's
 * guards. This module reads, mints ids, builds the §5.1 row and dispatches.
 *
 * ── The tap path, and where each step lives (§5.1) ───────────────────────────
 *
 * | Step | Where |
 * |---|---|
 * | 1–3 resolve `data-hid`/`data-role`, arm the hold, record the origin | `ui/capture/usePointerGesture.ts` |
 * | 4 pre-generate the id, derive the minute, build the whole `Observation` | {@link prepareIncrement} — here |
 * | 5 pressed state | CSS, applied imperatively by the row |
 * | 6–8 single-flight dispatch, one two-table transaction, ack on `complete` | `app/bus.ts` (U-4) |
 * | 9 ack class on the tile ref | the row's `onCommitted` |
 *
 * Step 4 belongs in `app/` and not in the component because it needs `newId()`; it is a plain
 * function rather than a hook so a Node test can assert the exact row a given pointerdown produces,
 * which is the only thing standing between a mis-resolved `data-hid` and a silent mis-attribution.
 */

/**
 * ── DISC-4's third sort term now reads a real column (§3.2, `v002`) ──────────
 *
 * This module previously exported `HYPOTHESIS_CREATED_AT_UNAVAILABLE = 0` and fed it to every row,
 * because `Hypothesis` had no creation timestamp while `order.ts`'s `DiscoveryRow` declared one.
 * U-8 was right to refuse to invent a timestamp and right to report it rather than decide it. The
 * design ruled (§3.2): **add the column**, backfill `0` — the sentinel's own value, which is what
 * makes the migration behaviour-preserving — and keep the sort term.
 *
 * The sentinel is gone rather than kept as an alias, because a constant named *unavailable* against
 * a column that is now always present is a broken window: the next reader cannot tell whether it
 * marks a real gap or a stale one. `0` survives as the migration's backfill, documented where the
 * migration is.
 */

/** A Discovery row as the pager renders it: DISC-4's sort keys, DISC-3's badge, §6.5's visuals. */
export interface DiscoveryHypothesisView extends DiscoveryPageRow {
  readonly text: string;
  /**
   * The row's own phase — finer than `discoveryBucket` (seven values mapped onto four pages).
   * Added for B-9's row anatomy (B design spec §2.4: phase icon per row); projected straight off
   * the stored column, beside the existing fields — additive, and A's pager ignores it.
   */
  readonly phase: HypothesisPhase;
  readonly status: Status;
  readonly statusOverride: StatusOverride;
  /** §5.4 — LIVE-6's window is a predicate over this committed column. The affordance is U-10's. */
  readonly lastIncrementTs: number | null;
  /** PRE-5 / §6.5 — the non-status carry badge. Affects neither status nor order. */
  readonly carriedStatus: Status | null;
  readonly carriedCount: number | null;
}

export interface DiscoveryContext {
  readonly match: MatchRow;
  /** §5.6's top zone shows *"clock · opponent · half"*, and the opponent is a name, not an id. */
  readonly opponent: string;
  readonly rows: readonly DiscoveryHypothesisView[];
}

/**
 * Everything the Discovery surface needs, from **one** readonly transaction.
 *
 * One transaction because the count badge and the row order are derived from two tables: a
 * hypothesis list read before an increment commits and an observation ledger read after it would
 * describe a moment that never existed, and the row would render a count its own first-observation
 * timestamp contradicts.
 *
 * ── Reading the ledger here, and never again ────────────────────────────────
 *
 * §5.1's protections include *"hot screens never subscribe to `observations`"*. This is a single
 * read on surface entry, not a subscription: `firstObservationCreatedAt` changes only when a
 * hypothesis goes from zero increments to one, which the caller already knows because it just
 * committed the increment. Re-reading the ledger per tap would put the whole match's observations in
 * front of the next tap's transaction.
 *
 * @returns `undefined` when the match has vanished — a normal outcome after a cascade, and the
 *   caller navigates rather than rendering an empty pager.
 * @throws {StorageError} on a read failure. Not swallowed: an empty board and a failed read look
 *   identical to the user, and only one of them is worth trying again.
 */
export async function loadDiscovery(matchId: string): Promise<DiscoveryContext | undefined> {
  return read(
    [TABLE.matches, TABLE.teams, TABLE.hypotheses, TABLE.observations],
    async (): Promise<DiscoveryContext | undefined> => {
      const match = await matches.getMatch(matchId);
      if (match === undefined) return undefined;

      const rows = await hypotheses.listHypothesesForMatch(matchId);
      const firstIncrementByHypothesis = firstIncrements(
        await observations.listObservationsForMatch(matchId),
      );

      return {
        match,
        // An unnamed opponent is a data fault, not a rendering decision — but the capture surface is
        // the wrong place to refuse over it, so it degrades to a label rather than an empty header.
        opponent: (await teams.getTeam(match.teamId))?.name ?? 'Opponent',
        rows: rows.map((row) => ({
          id: row.id,
          text: row.text,
          phase: row.phase,
          discoveryBucket: row.discoveryBucket,
          cachedCount: row.cachedCount,
          firstObservationCreatedAt: firstIncrementByHypothesis.get(row.id) ?? null,
          createdAt: row.createdAt,
          status: row.status,
          statusOverride: row.statusOverride,
          lastIncrementTs: row.lastIncrementTs,
          carriedStatus: row.carriedStatus,
          carriedCount: row.carriedCount,
        })),
      };
    },
    'loadDiscovery',
  );
}

/**
 * DISC-4's second sort term — the earliest surviving `increment` per hypothesis.
 *
 * "Surviving" is why this is derived from the ledger rather than stored: LIVE-6's undo deletes
 * increments (INV-1's one permitted deletion), and undoing a hypothesis back to zero must clear its
 * first-observation timestamp or it would keep a tie-break rank it no longer has evidence for.
 */
export function firstIncrements(ledger: readonly ObservationRow[]): Map<string, number> {
  const earliest = new Map<string, number>();
  for (const row of ledger) {
    if (row.kind !== 'increment' || row.hypothesisId === null) continue;
    const current = earliest.get(row.hypothesisId);
    if (current === undefined || row.createdAt < current) {
      earliest.set(row.hypothesisId, row.createdAt);
    }
  }
  return earliest;
}

/**
 * Whether taps on this surface record anything.
 *
 * `isInMatch`, the same predicate LIVE-6's window uses. PRE-7's *"Start in Discovery"* exit hands the
 * user here while the match is still `preMatch` (§6.0 — neither exit starts the clock), and an
 * increment then would stamp `matchMinute: 0` into the ledger for a match that has not kicked off.
 * The surface says so rather than dropping the tap silently; SES-1's Kickoff control is the locked
 * board's confirming hold (§6.4), which is build unit U-9's.
 */
export function canCapture(match: MatchRow): boolean {
  return isInMatch(match.state);
}

/**
 * §5.1 step 4 — build the whole `Increment` command at **pointerdown**.
 *
 * *"Pre-generate the ULID, call `deriveMatchMinute`, build the exact `Observation` row."* Two
 * consequences, both load-bearing:
 *
 * - The transaction spans `['observations','hypotheses']` and never reads a `matches` row, which is
 *   the whole of §5.1's budget argument. The minute is derived **here**, out of the anchors already
 *   in hand.
 * - `hypothesisId` is fixed at the moment the finger lands. DD-1's correctness layer for DISC-7 is
 *   exactly this: no reorder at any time, mid-animation included, can retarget a tap that has begun,
 *   because the target was resolved before the reorder could have happened.
 *
 * `at` is the pointerdown timestamp, so `createdAt` and `matchMinute` describe the moment the user
 * saw the thing rather than the moment the commit landed.
 */
export function prepareIncrement(input: {
  readonly match: MatchRow;
  readonly hypothesisId: string;
  readonly at: number;
}): IncrementCommand {
  const observation: ObservationRow = {
    id: newId(),
    hypothesisId: input.hypothesisId,
    matchId: input.match.id,
    matchMinute: deriveMatchMinute(input.match, input.match.state, input.at),
    kind: 'increment',
    audioBlobId: null,
    createdAt: input.at,
  };
  return {
    type: 'Increment',
    matchId: input.match.id,
    hypothesisId: input.hypothesisId,
    observation,
  };
}

/**
 * §5.1 steps 6–9 — dispatch the pre-built command and acknowledge **only** on commit.
 *
 * `onCommitted` is U-4's one acknowledgment point: `dispatch` hands it to `data/db.ts`'s `write()`,
 * which invokes it synchronously inside the native `IDBTransaction` `complete` listener. There is no
 * second call site, so a rejected handler, an aborted transaction or a commit event that never
 * arrives all leave it uncalled — SES-2 without a flag to remember to check.
 *
 * A-1 is settled (tap→ack p95 and p99 both 37 ms against NFR-1's 100 ms), so §19 F-9 does not fire
 * and there is deliberately **no** timeout branch that acknowledges anyway and no third "pending"
 * visual state (§5.3).
 */
export function commitIncrement(
  command: IncrementCommand,
  onCommitted: (at: number) => void,
): Promise<DispatchResult> {
  return dispatch(command, { onCommitted });
}

/**
 * LIVE-6 — undo one increment on one hypothesis, and acknowledge **only** on commit.
 *
 * Shared by both capture surfaces for the same reason `commitIncrement` is: the locked board and
 * Discovery run the same capture path, and a second entry point is a second place for SES-2's
 * acknowledgment rule to be got wrong. `onCommitted` is the same one call site — `write()` invokes
 * it inside the native `IDBTransaction` `complete` listener — so a rejected window, an aborted
 * transaction or a vanished row all leave it uncalled and nothing flashes.
 *
 * **No observation id crosses this boundary** (§5.4): the command names a hypothesis and the handler
 * chooses the row inside the transaction. That is LIVE-6's *"MUST NOT delete an increment belonging
 * to a different hypothesis"*, expressed as something the UI cannot say rather than something it is
 * trusted not to say.
 */
export function commitUndo(
  matchId: string,
  hypothesisId: string,
  onCommitted: (at: number) => void,
  /**
   * The clock the **window** is judged against, forwarded to the snapshot.
   *
   * Injectable for the reason `dispatch` already documents for SES-1's anchor window: LIVE-6's bound
   * is arithmetic on a wall clock, and a bound that only a real eight-second wait can exercise is a
   * bound no test asserts. Production omits it and the bus reads `Date.now()` at transaction time,
   * which is the value §5.4 requires — *not* the moment the finger landed.
   */
  now?: () => number,
): Promise<DispatchResult> {
  return dispatch({ type: 'UndoIncrement', matchId, hypothesisId }, { onCommitted, ...(now ? { now } : {}) });
}

/** PRE-2 / DISC-5 — the library the `＋` sheet offers. @throws {StorageError} on a read failure. */
export function loadLibrary(): Promise<TemplateRow[]> {
  return read([TABLE.templates], () => profile.listTemplates(), 'loadDiscoveryLibrary');
}

/**
 * DISC-5 — add one row mid-Discovery, list-only.
 *
 * `toHypothesisRow` is U-7's **one** constructor of a hypothesis from a candidate, reused rather
 * than reimplemented: its inheritance note names the risk precisely — a second constructor is a
 * second place that could assign a `boardPosition`. The command's `Decision<DiscoveryOp>` makes that
 * impossible anyway, and the two mechanisms are not redundant: one keeps the row correct, the other
 * keeps the op set correct.
 */
export function addDiscoveryHypothesis(
  matchId: string,
  candidate: PreMatchCandidate,
  createdAt: number = Date.now(),
): Promise<DispatchResult> {
  const command: AddDiscoveryHypothesisCommand = {
    type: 'AddDiscoveryHypothesis',
    matchId,
    // §3.2 — DISC-4's third sort term. One row, one moment: a row added mid-Discovery is genuinely
    // newer than everything already on the page, so it sorts to the tail of its count group, which
    // is where §6.1 says it belongs ("the new row appears on its bucket's page and, while the gate
    // is shut, at the tail"). The parameter has a default so the surface need not thread a clock,
    // and is injectable so a test can pin the value it asserts on.
    row: toHypothesisRow(matchId, candidate, createdAt),
  };
  return dispatch(command);
}
